import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE, getAuthHeaders, isAbortError } from "../api/client";
import {
  fetchSlotSyncState,
  fetchSoftoneActions,
  fetchSoftoneActionsCache,
  fetchWorkMonitor,
  logSlotSyncEvents,
  persistLocalAfterSuccessfulSend,
  persistLocalEvents,
  persistMonitorSnapshot,
  postAcceptDeletion,
  postClearSoftoneOwned,
  postFirstSend,
  postMarkSubmitted,
  postReplaceSlots,
  postUploadBlanks,
  refreshWorkMonitor,
  submittedSlotItems,
} from "../api/softoneSlotSync";
import { buildSoftoneShiftsFromPublishedRows, filterOpsTestEmployees, isOpsTestEmployeeCode, softoneStoreToFoxlineMap, softoneWritesDisabled } from "../lib/softone";
import {
  SLOT_CLASS,
  SLOT_LOG_EVENT,
  SLOT_LOG_SOURCE,
  attachActionAuditToMonitorSlots,
  canonicalSlotValue,
  classifySlot,
  foxlineMonitorRowToCanonical,
  groupSlotsByClass,
  isEmptySlot,
  mergeClassifiedSlots,
  preexistingOccupiedSlots,
  scheduleGridToSlotMap,
  digitSlotKey,
  slotKey,
  slotNeedsSoftonePush,
  slotNeedsSoftoneResend,
  slotSoftoneChangeLocked,
  slotsNeedingSoftonePush,
  slotsNeedingSoftoneResend,
  weekHasFirstSend,
  indexHistoryBySlot,
  formatSoftoneWriteNotice,
} from "../lib/softoneSlotSync";
import { toLocalISO } from "../lib/dates";
import { rosterScheduleKey } from "../employeeMatch";

function publishedRowsFromSlots(slots) {
  return (slots || [])
    .map((slot) => {
      const value = canonicalSlotValue(slot.local || slot.value);
      if (value === "OFF") {
        return {
          status: "published",
          rota_date: slot.date,
          employee_code: slot.employee_code,
          is_off: true,
        };
      }
      if (value === "NO_WORK") {
        return {
          status: "no_work",
          rota_date: slot.date,
          employee_code: slot.employee_code,
          is_off: false,
        };
      }
      const m = value.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
      if (!m) return null;
      return {
        status: "published",
        rota_date: slot.date,
        employee_code: slot.employee_code,
        shift_start: m[1],
        shift_end: m[2],
      };
    })
    .filter(Boolean);
}

function eventForSlot(slot, changeType, source, user, store, weekStart, extra = {}) {
  return {
    store_name: store,
    week_start: weekStart,
    employee_code: slot.employee_code,
    employee_name: slot.employee_name,
    employee_id: slot.employee_id,
    date: slot.date,
    change_type: changeType,
    source,
    old_value: extra.old_value !== undefined ? extra.old_value : slot.submitted,
    new_value: extra.new_value !== undefined ? extra.new_value : slot.local,
    changed_by_name: user?.name,
    changed_by_role: user?.role,
  };
}

export function useFoxlineSlotSync({
  store,
  weekStartISO,
  weekDates,
  dayKeyFn,
  localSchedule,
  staffPool,
  storeRecords,
  user,
  enabled = true,
  onWriteComplete,
  /** When false, SoftOne API writes are blocked (week not yet at pending_hr). */
  softoneWritesAllowed = false,
  /** Called after refresh clears SoftOne-owned Internal rows in dp_staff_rota. */
  onInternalCleared = null,
  lang = "en",
}) {
  const [snapshot, setSnapshot] = useState(null);
  const [monitor, setMonitor] = useState({
    available: false,
    slots: [],
    fetched_at: null,
    missing: false,
    cached: false,
    age_seconds: null,
    source: null,
  });
  const [actionsCache, setActionsCache] = useState({
    available: false,
    fetched_at: null,
    actions: [],
    cached: false,
  });
  const [loading, setLoading] = useState(false);
  const [actionState, setActionState] = useState("idle");
  const [busyKind, setBusyKind] = useState(null);
  const [transmitting, setTransmitting] = useState(false);
  const pendingClearTransmitRef = useRef(false);
  const [error, setError] = useState("");
  const [writeNotice, setWriteNotice] = useState(null);
  const [locationsByStore, setLocationsByStore] = useState({});
  const [readOnlyFromLocations, setReadOnlyFromLocations] = useState(false);
  const [softoneTesting, setSoftoneTesting] = useState(false);
  const [changeRows, setChangeRows] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);
  const lockedClearKeysRef = useRef(new Set());
  const unifiedLocationId = useMemo(() => {
    const rec = (storeRecords || []).find(
      (r) => String(r.location_name || "").trim() === String(store || "").trim()
    );
    return rec?.unified_location_id ?? null;
  }, [storeRecords, store]);

  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  useEffect(() => {
    lockedClearKeysRef.current = new Set();
  }, [store, weekStartISO]);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    fetch(`${API_BASE}/api/softone/locations`, { headers: getAuthHeaders(), signal: controller.signal })
      .then((r) => (r.ok ? r.json() : { locations: [] }))
      .then((data) => {
        if (controller.signal.aborted) return;
        setLocationsByStore(softoneStoreToFoxlineMap(data));
        setReadOnlyFromLocations(softoneWritesDisabled(data));
        setSoftoneTesting(Boolean(data?.testing));
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setLocationsByStore({});
        setSoftoneTesting(false);
      });
    return () => controller.abort();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !store || store === "All Stores" || !weekStartISO) return undefined;
    const locId = unifiedLocationId;
    if (locId == null) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError("");
    Promise.all([
      fetchSlotSyncState(store, weekStartISO, { signal, unifiedLocationId: locId }),
      fetchWorkMonitor(store, weekStartISO, { signal, unifiedLocationId: locId }).catch((err) => {
        if (isAbortError(err)) throw err;
        return {
          available: false,
          slots: [],
          fetched_at: null,
          error: err.message,
        };
      }),
      fetchSoftoneActionsCache(store, weekStartISO, { signal, unifiedLocationId: locId }).catch((err) => {
        if (isAbortError(err)) throw err;
        return { available: false, fetched_at: null, actions: [] };
      }),
      fetch(
        `${API_BASE}/schedule/changes?unified_location_id=${encodeURIComponent(locId)}&week_start=${encodeURIComponent(weekStartISO)}`,
        { headers: getAuthHeaders(), signal }
      )
        .then((r) => (r.ok ? r.json() : []))
        .catch((err) => {
          if (isAbortError(err)) throw err;
          return [];
        }),
    ])
      .then(([state, mon, actions, changes]) => {
        if (signal.aborted) return;
        if (mon.available) persistMonitorSnapshot(store, weekStartISO, mon);
        setSnapshot(state);
        setMonitor(mon);
        setActionsCache(actions);
        setChangeRows(Array.isArray(changes) ? changes : []);
        if (mon.error) setError(mon.error);
        const cleared = Array.isArray(mon.cleared_internal) ? mon.cleared_internal : [];
        if (cleared.length && typeof onInternalCleared === "function") {
          onInternalCleared(cleared);
        }
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setError(err.message || "Failed to load SoftOne slot sync");
      })
      .finally(() => {
        if (signal.aborted) return;
        setLoading(false);
        if (pendingClearTransmitRef.current) {
          pendingClearTransmitRef.current = false;
          setTransmitting(false);
        }
      });
    return () => controller.abort();
  }, [enabled, store, unifiedLocationId, weekStartISO, reloadTick, onInternalCleared]);

  useEffect(() => {
    setWriteNotice(null);
  }, [store, weekStartISO]);

  const localMap = useMemo(() => {
    if (!weekDates || !dayKeyFn) return {};
    return scheduleGridToSlotMap(localSchedule || {}, weekDates, dayKeyFn, toLocalISO);
  }, [localSchedule, weekDates, dayKeyFn]);

  const classifiedSlots = useMemo(() => {
    let monitorSlots = filterOpsTestEmployees(
      monitor.available ? monitor.slots : [],
      { testing: softoneTesting }
    );
    // Ensure desktop SoftOne ownership is visible even when audit arrived via actions cache.
    if (actionsCache?.actions?.length) {
      monitorSlots = attachActionAuditToMonitorSlots(monitorSlots, actionsCache.actions);
    }
    const snapshotSlots = filterOpsTestEmployees(snapshot?.slots || [], {
      testing: softoneTesting,
    });
    const slots = mergeClassifiedSlots({
      localMap,
      snapshotSlots,
      monitorSlots,
      monitorComplete: Boolean(monitor.available),
    }).filter((s) => softoneTesting || !isOpsTestEmployeeCode(s.employee_code));
    const nameByCode = {};
    const idByCode = {};
    (staffPool || []).forEach((emp) => {
      const code = String(rosterScheduleKey(emp) || emp.employee_code || emp.employeeCode || emp.afm || "").trim();
      if (!code) return;
      nameByCode[code] = emp.name || emp.full_name || code;
      idByCode[code] = emp.id ?? emp.employee_id ?? null;
    });
    return slots.map((s) => ({
      ...s,
      employee_name: nameByCode[s.employee_code] || s.employee_code,
      employee_id: idByCode[s.employee_code] ?? s.employee_code,
    }));
  }, [localMap, snapshot, monitor, actionsCache, staffPool, softoneTesting]);

  // Locked SoftOne-owned + Internal still set → delete Internal immediately (UI + API).
  useEffect(() => {
    if (!enabled || !store || store === "All Stores" || !weekStartISO) return;
    const pending = (classifiedSlots || []).filter((s) => {
      if (!slotSoftoneChangeLocked(s) || isEmptySlot(s.local)) return false;
      const code = String(s.employee_code || "").trim();
      const date = String(s.date || "").slice(0, 10);
      if (!code || !date) return false;
      return !lockedClearKeysRef.current.has(`${code}::${date}`);
    });
    if (!pending.length) return;
    pending.forEach((s) => {
      lockedClearKeysRef.current.add(
        `${String(s.employee_code || "").trim()}::${String(s.date || "").slice(0, 10)}`
      );
    });
    const cleared = pending.map((s) => ({
      employee_code: s.employee_code,
      date: s.date,
      old_value: s.local,
      softone_value: s.foxline,
    }));
    if (typeof onInternalCleared === "function") {
      onInternalCleared(cleared);
    }
    postClearSoftoneOwned(store, weekStartISO, { unifiedLocationId })
      .then((result) => {
        const fromApi = Array.isArray(result?.cleared_internal) ? result.cleared_internal : [];
        if (fromApi.length && typeof onInternalCleared === "function") {
          onInternalCleared(fromApi);
        }
      })
      .catch(() => {
        /* UI already cleared; API retry on next SoftOne load */
      });
  }, [
    classifiedSlots,
    enabled,
    store,
    weekStartISO,
    unifiedLocationId,
    onInternalCleared,
  ]);

  const grouped = useMemo(() => groupSlotsByClass(classifiedSlots), [classifiedSlots]);
  const firstSent = weekHasFirstSend(classifiedSlots, snapshot?.first_sent_at);
  const preexisting = useMemo(() => preexistingOccupiedSlots(classifiedSlots), [classifiedSlots]);
  const slotByKey = useMemo(() => {
    const map = {};
    classifiedSlots.forEach((s) => {
      map[s.key] = s;
      const digKey = digitSlotKey(s.employee_code, s.date);
      if (digKey && digKey !== s.key) map[digKey] = s;
    });
    return map;
  }, [classifiedSlots]);

  const historyBySlot = useMemo(
    () => indexHistoryBySlot([...(changeRows || []), ...(snapshot?.events || [])], classifiedSlots),
    [changeRows, snapshot, classifiedSlots]
  );

  const locationId = locationsByStore[store] ?? null;
  const readOnly = Boolean(
    readOnlyFromLocations ||
      softoneWritesDisabled(snapshot) ||
      softoneWritesDisabled(monitor)
  );

  const runAndReload = useCallback(
    async (fn, { transmit = false, kind = null } = {}) => {
      if (transmit) {
        pendingClearTransmitRef.current = false;
        setTransmitting(true);
      } else {
        setActionState("loading");
        setBusyKind(kind);
      }
      setError("");
      setWriteNotice(null);
      try {
        const result = await fn();
        const notice = formatSoftoneWriteNotice(result, lang);
        if (notice) setWriteNotice(notice);
        if (transmit) pendingClearTransmitRef.current = true;
        reload();
        if (transmit && typeof onWriteComplete === "function") {
          try {
            onWriteComplete(result);
          } catch {
            /* builder refresh must not fail the SoftOne write */
          }
        }
        return result;
      } catch (err) {
        setError(err.message || "Action failed");
        if (transmit) {
          pendingClearTransmitRef.current = false;
          setTransmitting(false);
        }
        throw err;
      } finally {
        if (!transmit) {
          setActionState("idle");
          setBusyKind(null);
        }
      }
    },
    [reload, onWriteComplete, lang]
  );

  const refreshMonitor = useCallback(async () => {
    return runAndReload(async () => {
      const mon = await refreshWorkMonitor(store, weekStartISO, { unifiedLocationId });
      if (mon.available) {
        const prev = snapshot?.slots || [];
        const events = [];
        (mon.slots || []).forEach((row) => {
          const code = row.employee_code || row.employeeCode;
          const date = String(row.date || row.work_date || "").slice(0, 10);
          if (!code || !date) return;
          const prevSnap = prev.find((s) => s.employee_code === code && s.date === date);
          const nextFox = foxlineMonitorRowToCanonical(row);
          const submitted = canonicalSlotValue(prevSnap?.submitted);
          if (isEmptySlot(submitted)) return;
          const prevClass = classifySlot({
            submitted,
            foxline: prevSnap?.foxline,
            local: localMap[`${code}::${date}`],
          });
          const nextClass = classifySlot({
            submitted,
            foxline: nextFox,
            local: localMap[`${code}::${date}`],
          });
          if (prevClass === nextClass && canonicalSlotValue(prevSnap?.foxline) === canonicalSlotValue(nextFox)) return;
          if (nextClass === SLOT_CLASS.DELETED_IN_SOFTONE || nextClass === SLOT_CLASS.FOXLINE_CHANGED || nextClass === SLOT_CLASS.CONFLICT) {
            events.push(
              eventForSlot(
                { employee_code: code, date, submitted, local: localMap[`${code}::${date}`] },
                nextClass === SLOT_CLASS.DELETED_IN_SOFTONE ? SLOT_LOG_EVENT.FOXLINE_DELETED : SLOT_LOG_EVENT.FOXLINE_CHANGED,
                SLOT_LOG_SOURCE.FOXLINE,
                user,
                store,
                weekStartISO,
                { old_value: submitted, new_value: nextFox }
              )
            );
          }
        });
        persistMonitorSnapshot(store, weekStartISO, mon);
        if (events.length) {
          persistLocalEvents(store, weekStartISO, events);
          logSlotSyncEvents(events);
        }
        const cleared = Array.isArray(mon.cleared_internal) ? mon.cleared_internal : [];
        if (cleared.length && typeof onInternalCleared === "function") {
          onInternalCleared(cleared);
        }
      }
      // Same button also refreshes daily-report audit (WEB vs desktop).
      // Desktop SoftOne ownership clears happen here once action_audit is fresh.
      setBusyKind("actions");
      try {
        const actions = await fetchSoftoneActions(store, weekStartISO, { unifiedLocationId });
        setActionsCache(actions);
        const clearedAfterActions = Array.isArray(actions?.cleared_internal)
          ? actions.cleared_internal
          : [];
        if (clearedAfterActions.length && typeof onInternalCleared === "function") {
          onInternalCleared(clearedAfterActions);
        }
      } catch (actionsErr) {
        // Monitor succeeded; surface actions failure without dropping monitor result.
        setError(actionsErr.message || "Failed to fetch SoftOne actions");
      }
      return mon;
    }, { kind: "monitor" });
  }, [runAndReload, store, weekStartISO, unifiedLocationId, snapshot, localMap, user, onInternalCleared]);

  const fetchActions = useCallback(async () => {
    return runAndReload(async () => {
      const result = await fetchSoftoneActions(store, weekStartISO, { unifiedLocationId });
      setActionsCache(result);
      const cleared = Array.isArray(result?.cleared_internal) ? result.cleared_internal : [];
      if (cleared.length && typeof onInternalCleared === "function") {
        onInternalCleared(cleared);
      }
      return result;
    }, { kind: "actions" });
  }, [runAndReload, store, weekStartISO, unifiedLocationId, onInternalCleared]);

  const sendSlots = useCallback(
    async (slotsToSend, { firstSend = false, restore = false, replace = false } = {}) => {
      if (readOnly) {
        throw new Error("SoftOne uploads are disabled in prod simulation (read-only).");
      }
      if (!softoneWritesAllowed) {
        throw new Error(
          "SoftOne upload is only available after T3 approval (Pending HR upload)."
        );
      }
      const sendable = (slotsToSend || []).filter((s) => !isEmptySlot(s.local || s.value));
      if (sendable.length === 0) {
        throw new Error("No sendable slots");
      }
      const rows = publishedRowsFromSlots(sendable);
      const shifts = buildSoftoneShiftsFromPublishedRows(
        rows,
        store,
        locationsByStore,
        staffPool,
        storeRecords
      );
      const slotItems = sendable.map((s) => ({
        employee_code: s.employee_code,
        date: s.date,
        value: canonicalSlotValue(s.local || s.value),
      }));
      if (firstSend) {
        const result = await postFirstSend({
          storeId: store,
          weekStart: weekStartISO,
          locationId,
          submittedBy: Number(user?.id) || user?.id,
          user,
          shifts,
          slotItems,
          sendEmptyOnly: true,
        });
        const accepted = submittedSlotItems(slotItems, result);
        if (accepted.length) {
          persistLocalAfterSuccessfulSend(store, weekStartISO, accepted, user, { firstSend: true });
          const acceptedKeys = new Set(accepted.map((s) => slotKey(s.employee_code, s.date)));
          const events = sendable
            .filter((s) => acceptedKeys.has(s.key) || acceptedKeys.has(slotKey(s.employee_code, s.date)))
            .map((s) =>
              eventForSlot(s, SLOT_LOG_EVENT.FIRST_SEND, SLOT_LOG_SOURCE.UPLOAD, user, store, weekStartISO, {
                old_value: "",
                new_value: s.local,
              })
            );
          persistLocalEvents(store, weekStartISO, events);
          logSlotSyncEvents(events);
        }
        if (Number(result?.submitted || result?.data?.submitted || 0) > 0) {
          await refreshWorkMonitor(store, weekStartISO, { unifiedLocationId }).catch(() => null);
        }
        return result;
      }
      const postFn = replace ? postReplaceSlots : postUploadBlanks;
      const result = await postFn({
        storeId: store,
        weekStart: weekStartISO,
        locationId,
        submittedBy: Number(user?.id) || user?.id,
        shifts,
        slotItems,
      });
      const accepted = submittedSlotItems(slotItems, result);
      if (accepted.length) {
        persistLocalAfterSuccessfulSend(store, weekStartISO, accepted, user);
        const acceptedKeys = new Set(accepted.map((s) => slotKey(s.employee_code, s.date)));
        const eventType = replace
          ? SLOT_LOG_EVENT.REPLACE_VIA_API
          : restore
            ? SLOT_LOG_EVENT.RESTORE_VIA_API
            : SLOT_LOG_EVENT.API_BLANK_UPLOAD;
        const events = sendable
          .filter((s) => acceptedKeys.has(s.key) || acceptedKeys.has(slotKey(s.employee_code, s.date)))
          .map((s) =>
            eventForSlot(
              s,
              eventType,
              SLOT_LOG_SOURCE.UPLOAD,
              user,
              store,
              weekStartISO,
              { old_value: s.submitted, new_value: s.local }
            )
          );
        persistLocalEvents(store, weekStartISO, events);
        logSlotSyncEvents(events);
      }
      if (Number(result?.submitted || result?.data?.submitted || 0) > 0) {
        await refreshWorkMonitor(store, weekStartISO, { unifiedLocationId }).catch(() => null);
      }
      return result;
    },
    [
      store,
      weekStartISO,
      unifiedLocationId,
      locationsByStore,
      staffPool,
      storeRecords,
      locationId,
      user,
      readOnly,
      softoneWritesAllowed,
    ]
  );

  const firstSend = useCallback(
    async () => {
      return runAndReload(async () => {
        const toSend = classifiedSlots.filter((s) => s.classification === SLOT_CLASS.API_ELIGIBLE);
        return sendSlots(toSend, { firstSend: !firstSent });
      }, { transmit: true });
    },
    [runAndReload, firstSent, classifiedSlots, sendSlots]
  );

  const uploadEligible = useCallback(
    async (keys) => {
      return runAndReload(async () => {
        const set = keys ? new Set(keys) : null;
        const toSend = classifiedSlots.filter(
          (s) => s.classification === SLOT_CLASS.API_ELIGIBLE && (!set || set.has(s.key))
        );
        return sendSlots(toSend, { firstSend: false });
      }, { transmit: true });
    },
    [runAndReload, classifiedSlots, sendSlots]
  );

  const restoreDeleted = useCallback(
    async (keys) => {
      return runAndReload(async () => {
        const set = new Set(keys || []);
        const toSend = classifiedSlots.filter(
          (s) => s.classification === SLOT_CLASS.DELETED_IN_SOFTONE && set.has(s.key) && !isEmptySlot(s.local)
        );
        return sendSlots(toSend, { restore: true });
      }, { transmit: true });
    },
    [runAndReload, classifiedSlots, sendSlots]
  );

  const replaceModified = useCallback(
    async (keys) => {
      return runAndReload(async () => {
        const set = keys ? new Set(keys) : null;
        const toSend = classifiedSlots.filter(
          (s) => (!set || set.has(s.key)) && slotNeedsSoftoneResend(s)
        );
        return sendSlots(toSend, { replace: true });
      }, { transmit: true });
    },
    [runAndReload, classifiedSlots, sendSlots]
  );

  /** Push every Internal≠SoftOne slot: resend occupied, upload blanks. */
  const pushSoftoneDiffs = useCallback(
    async (keys) => {
      return runAndReload(async () => {
        const set = keys ? new Set(keys) : null;
        const diffs = classifiedSlots.filter(
          (s) => (!set || set.has(s.key)) && slotNeedsSoftonePush(s)
        );
        const toReplace = diffs.filter((s) => !isEmptySlot(s.foxline));
        const toUpload = diffs.filter((s) => isEmptySlot(s.foxline));
        let last = { submitted: 0, failed: 0, skipped: 0 };
        if (toReplace.length) {
          last = await sendSlots(toReplace, { replace: true });
        }
        if (toUpload.length) {
          const uploaded = await sendSlots(toUpload, { firstSend: false });
          const errors = [
            ...(last?.errors || last?.data?.errors || []),
            ...(uploaded?.errors || uploaded?.data?.errors || []),
          ];
          const results = [
            ...(last?.results || last?.data?.results || []),
            ...(uploaded?.results || uploaded?.data?.results || []),
          ];
          last = {
            submitted: Number(last?.submitted || last?.data?.submitted || 0)
              + Number(uploaded?.submitted || uploaded?.data?.submitted || 0),
            failed: Number(last?.failed || last?.data?.failed || 0)
              + Number(uploaded?.failed || uploaded?.data?.failed || 0),
            skipped: Number(last?.skipped || last?.data?.skipped || 0)
              + Number(uploaded?.skipped || uploaded?.data?.skipped || 0),
            errors,
            results,
            data: {
              ...(last?.data || {}),
              ...(uploaded?.data || {}),
              submitted: Number(last?.submitted || last?.data?.submitted || 0)
                + Number(uploaded?.submitted || uploaded?.data?.submitted || 0),
              failed: Number(last?.failed || last?.data?.failed || 0)
                + Number(uploaded?.failed || uploaded?.data?.failed || 0),
              skipped: Number(last?.skipped || last?.data?.skipped || 0)
                + Number(uploaded?.skipped || uploaded?.data?.skipped || 0),
              errors,
              results,
              ergani_standin:
                uploaded?.data?.ergani_standin || last?.data?.ergani_standin || null,
              week: uploaded?.data?.week || last?.data?.week || null,
            },
            ergani_standin:
              uploaded?.data?.ergani_standin
              || uploaded?.ergani_standin
              || last?.data?.ergani_standin
              || last?.ergani_standin
              || null,
            week: uploaded?.data?.week || uploaded?.week || last?.data?.week || last?.week || null,
          };
        }
        return last;
      }, { transmit: true });
    },
    [runAndReload, classifiedSlots, sendSlots]
  );

  const markSubmitted = useCallback(
    async (keys) => {
      return runAndReload(async () => {
        const set = new Set(keys || []);
        const items = classifiedSlots.filter((s) => set.has(s.key));
        const slotItems = items.map((s) => ({
          employee_code: s.employee_code,
          date: s.date,
          value: s.classification === SLOT_CLASS.LOCAL_MATCHES_FOXLINE ? s.foxline : s.local,
        }));
        const result = await postMarkSubmitted({ storeId: store, weekStart: weekStartISO, slotItems, user });
        const events = items.map((s) =>
          eventForSlot(s, SLOT_LOG_EVENT.MARK_SUBMITTED, SLOT_LOG_SOURCE.UPLOAD, user, store, weekStartISO, {
            old_value: s.submitted,
            new_value: s.classification === SLOT_CLASS.LOCAL_MATCHES_FOXLINE ? s.foxline : s.local,
          })
        );
        persistLocalEvents(store, weekStartISO, events);
        logSlotSyncEvents(events);
        return result;
      });
    },
    [runAndReload, classifiedSlots, store, weekStartISO, user]
  );

  const acceptDeletion = useCallback(
    async (keys) => {
      return runAndReload(async () => {
        const set = new Set(keys || []);
        const items = classifiedSlots.filter((s) => set.has(s.key));
        const slotItems = items.map((s) => ({ employee_code: s.employee_code, date: s.date, value: "" }));
        const result = await postAcceptDeletion({ storeId: store, weekStart: weekStartISO, slotItems, user });
        const events = items.map((s) =>
          eventForSlot(s, SLOT_LOG_EVENT.ACCEPT_DELETION, SLOT_LOG_SOURCE.UPLOAD, user, store, weekStartISO, {
            old_value: s.submitted,
            new_value: "",
          })
        );
        persistLocalEvents(store, weekStartISO, events);
        logSlotSyncEvents(events);
        return result;
      });
    },
    [runAndReload, classifiedSlots, store, weekStartISO, user]
  );

  const recordLocalEdits = useCallback(
    (items) => {
      if (!store || store === "All Stores" || !weekStartISO) return;
      const events = (items || [])
        .map((item) => {
          const code = String(item.employee_code || "").trim();
          const date = String(item.rota_date || item.date || "").slice(0, 10);
          if (!code || !date) return null;
          const changeType = String(item.change_type || SLOT_LOG_EVENT.MODIFIED).toLowerCase();
          const type =
            changeType === "assigned" || changeType === "assign"
              ? SLOT_LOG_EVENT.ASSIGNED
              : changeType === "removed" || changeType === "remove"
                ? SLOT_LOG_EVENT.REMOVED
                : SLOT_LOG_EVENT.MODIFIED;
          return eventForSlot(
            {
              employee_code: code,
              employee_name: item.employee_name,
              employee_id: item.employee_id,
              date,
              submitted: item.old_value,
              local: item.new_value,
            },
            type,
            SLOT_LOG_SOURCE.LOCAL,
            user,
            store,
            weekStartISO,
            { old_value: item.old_value ?? "", new_value: item.new_value ?? "" }
          );
        })
        .filter(Boolean);
      if (!events.length) return;
      const written = persistLocalEvents(store, weekStartISO, events);
      setSnapshot((prev) => ({
        ...(prev || {}),
        ...written,
        slots: prev?.slots || written.slots,
        events: written.events,
        persistence: prev?.persistence || written.persistence,
      }));
    },
    [store, weekStartISO, user]
  );

  return {
    loading,
    transmitting,
    actionBusy: actionState === "loading" || transmitting,
    refreshingMonitor: busyKind === "monitor",
    fetchingActions: busyKind === "actions",
    error,
    writeNotice,
    snapshot,
    monitor,
    actionsCache,
    persistence: snapshot?.persistence || "local",
    classifiedSlots,
    grouped,
    slotByKey,
    historyBySlot,
    firstSent,
    preexisting,
    locationId,
    readOnly,
    testing: softoneTesting,
    softoneWritesAllowed: Boolean(softoneWritesAllowed) && !readOnly,
    changeRows,
    refreshMonitor,
    fetchActions,
    firstSend,
    uploadEligible,
    restoreDeleted,
    replaceModified,
    pushSoftoneDiffs,
    markSubmitted,
    acceptDeletion,
    recordLocalEdits,
    reload,
    resendCount: slotsNeedingSoftoneResend(classifiedSlots).length,
    pushDiffCount: slotsNeedingSoftonePush(classifiedSlots).length,
    lockedCount: classifiedSlots.filter(slotSoftoneChangeLocked).length,
  };
}
